import { Map, Set, OrderedMap } from 'immutable';

import { MetricsRecord } from '../../../records';
import {
  clearSelectedVersion,
  getInitAggregatedNames,
  parseAggregatedMetrics,
} from './utils';

export const createTestOverviewReducers = (types) => {
  const initialState = Map({
    trends: OrderedMap({
      status: Set(),
      browser: Set(),
      browserError: Set(),
      browserFail: Set(),
      os: Set(),
      osError: Set(),
      osFail: Set(),
      statusRecord: OrderedMap({
        totalRuns: new MetricsRecord({
          name: 'total-tests',
          title: 'Total Runs',
          labelClassName: 'blue',
        }),
        passed: new MetricsRecord({
          title: 'Passed',
          labelClassName: 'success',
        }),
        error: new MetricsRecord({
          title: 'Error',
          labelClassName: 'warning',
        }),
        failed: new MetricsRecord({
          title: 'Failed',
          labelClassName: 'danger',
        }),
        complete: new MetricsRecord({
          title: 'Completed',
        }),
      }),
    }),
    selectedVersion: Map({
      title: null,
      items: [],
    }),
    aggregatedNames: getInitAggregatedNames(),
    testHealth: Map({
      totalCases: new MetricsRecord({
        name: 'Total Cases',
      }),
      totalCasesLimited: new MetricsRecord({
        name: 'Total Cases Limited',
      }),
      failing: new MetricsRecord({
        name: 'Consistently Failing',
      }),
      passing: new MetricsRecord({
        name: 'Consistently Passing',
      }),
      error: new MetricsRecord({
        name: 'Consistently Error',
      }),
      missingStatus: new MetricsRecord({
        name: 'Missing Status',
      }),
    }),
    fadeMetrics: true,
    loadingData: true,
    loadingTestHealth: false,
  });

  const filterStatusRecord = ([key]) => initialState.getIn(['trends', 'statusRecord']).has(key);

  const reducer = (state = initialState, { type, payload }) => {
    switch (type) {
      case types.GET_TEST_TRENDS.REQUEST:
        return state.set('fadeMetrics', false).set('loadingData', true);
      case types.GET_TEST_TRENDS.SUCCESS:
        return state.withMutations((newState) => {
          clearSelectedVersion(newState);
          parseAggregatedMetrics(newState, payload.metrics);

          Object.entries(payload.metrics).map(([key, metrics]) => newState.setIn(
            ['trends', key],
            Set(
              Object
                .entries(metrics)
                .map(([title, value]) => ({ title, value })),
            ),
          ));

          let totalTests = 0;
          if (payload.metrics.status) {
            Object.entries(payload.metrics.status)
              .filter(filterStatusRecord)
              .forEach(([key, value]) => {
                totalTests += value;
                newState.setIn(['trends', 'statusRecord', key, 'value'], value);
              });
          }
          newState.setIn(['trends', 'statusRecord', 'totalRuns', 'value'], totalTests);
          newState.set('fadeMetrics', true).set('loadingData', false);
        });
      case types.GET_TEST_TRENDS.FAILURE:
        return state.set('loadingData', false);
      case types.GET_PAST_TRENDS.SUCCESS:
        return state.withMutations((newState) => {
          let totalTests = 0;
          if (payload.metrics.status) {
            Object.entries(payload.metrics.status)
              .filter(filterStatusRecord)
              .forEach(([key, value]) => {
                totalTests += value;
                newState.setIn(['trends', 'statusRecord', key, 'previousValue'], value);
              });
          }
          newState.setIn(['trends', 'statusRecord', 'totalRuns', 'previousValue'], totalTests);
        });
      case types.GET_TEST_HEALTH.REQUEST:
        return state.withMutations((newState) => {
          newState
            .set('loadingTestHealth', true)
            .set('testHealth', initialState.get('testHealth'));
        });
      case types.GET_TEST_HEALTH.SUCCESS:
        return state.withMutations((newState) => {
          newState.setIn(['testHealth', 'totalCases', 'value'], payload.total_test_cases);
          newState.setIn(['testHealth', 'totalCasesLimited', 'value'], payload.total_test_cases_limited);
          newState.setIn(['testHealth', 'failing', 'value'], payload.consistently_failing);
          newState.setIn(['testHealth', 'passing', 'value'], payload.consistently_passing);
          newState.setIn(['testHealth', 'error', 'value'], payload.consistently_error);
          newState.setIn(['testHealth', 'missingStatus', 'value'], payload.consistently_complete);
          newState.set('loadingTestHealth', false);
        });
      case types.GET_TEST_HEALTH.FAILURE:
        return state.set('loadingTestHealth', false);
      case types.GET_PAST_HEALTH.SUCCESS:
        return state.withMutations((newState) => {
          newState.setIn(['testHealth', 'totalCases', 'previousValue'], payload.total_test_cases);
          newState.setIn(['testHealth', 'totalCasesLimited', 'previousValue'], payload.total_test_cases_limited);
          newState.setIn(['testHealth', 'failing', 'previousValue'], payload.consistently_failing);
          newState.setIn(['testHealth', 'passing', 'previousValue'], payload.consistently_passing);
          newState.setIn(['testHealth', 'error', 'previousValue'], payload.consistently_error);
          newState.setIn(['testHealth', 'missingStatus', 'previousValue'], payload.consistently_complete);
        });
      case types.SELECT_BY_VERSION:
        return state.set('selectedVersion', Map(payload));
      case types.CLEAR_SELECT_BY_VERSION:
        return clearSelectedVersion(state);
      case types.CLEANUP_OVERVIEW:
      case types.CLEANUP_TEST_SUMMARY:
        return initialState;
      default:
        return state;
    }
  };
  return { initialState, reducer };
};
